import { Box, Button } from '@mui/material';
import { useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { observer } from 'mobx-react';
import FormHandler from './FormHandler';
import FieldGenerator from './FieldGenerator';
import { useLocalization } from '../../providers/LocalizationProvider';
import { useStore } from '../../providers/MobxProvider';
import getDefaultValuesForForm from './lib/getDefaultValuesForForm';

function FormSearchButtons({
  onSubmitClick,
  onHideClick,
  onClearClick,
  IconLibrary = {},
}) {
  const { formatMessage } = useLocalization();
  return (
    <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
      <Button
        variant="contained"
        startIcon={<IconLibrary.Search />}
        color="primary"
        onClick={() => onSubmitClick()}>
        {formatMessage({
          id: 'SEARCH',
          defaultMessage: 'Search',
        })}
      </Button>

      <Button
        variant="contained"
        color="primary"
        startIcon={<IconLibrary.ClearSearch />}
        onClick={() => {
          onClearClick();
        }}>
        {formatMessage({
          id: 'CLEAR',
          defaultMessage: 'Clear',
        })}
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<IconLibrary.HideSearch />}
        onClick={() => onHideClick()}>
        {formatMessage({
          id: 'HIDE',
          defaultMessage: 'Hide',
        })}
      </Button>
    </Box>
  );
}

const FormSearchAdvanced = forwardRef(
  (
    {
      schema,
      onClear = undefined,
      onSubmit = undefined,
      hideSearchForm = undefined,
      validationSchema = null,
      customComponents = {},
      IconLibrary = {},
      initialValues,
      children,
      storeName,
      getValueByControl = null,
      noLabels = false,
    },
    ref,
  ) => {
    const formRef = useRef();
    const store = useStore();

    if (!storeName) {
      throw Error('Store Name Not provided');
    }

    const { filters = [] } = store.domain.list.get(storeName);

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (formRef.current) {
          formRef.current.submit();
        }
      },
      reset: () => {
        if (formRef.current) {
          formRef.current.reset();
        }
      },
    }));

    const defaultInitialValuesFromSchema = getDefaultValuesForForm({
      schema,
      ...(getValueByControl && { getValueByControl }),
    });

    const defaultvalues = useMemo(() => {
      return filters?.reduce(
        (acc, filter) => ({
          ...acc,
          [filter?.field]: filter?.value,
        }),
        { ...defaultInitialValuesFromSchema, ...initialValues },
      );
    }, [filters, defaultInitialValuesFromSchema, initialValues]);

    return (
      <Box>
        <FormHandler
          isSearchForm
          formSchema={schema}
          disableUnSavedCheck
          ref={formRef}
          validationSchema={validationSchema}
          initialValues={defaultvalues || {}}
          onSubmit={onSubmit}>
          <div className="form-fields">
            <div className="form-field-container">
              <FieldGenerator
                customComponents={customComponents}
                schema={schema}
                noLabels={noLabels}
              />
            </div>
            {children ? (
              <div className="form-field-buttons">{children}</div>
            ) : (
              <FormSearchButtons
                IconLibrary={IconLibrary}
                formRef={formRef}
                onClearClick={() => {
                  onClear();
                  formRef.current.reset();
                }}
                onSubmitClick={() => formRef.current.submit()}
                onHideClick={() => {
                  if (hideSearchForm) {
                    hideSearchForm();
                  }
                }}
              />
            )}
          </div>
        </FormHandler>
      </Box>
    );
  },
);

export default observer(FormSearchAdvanced);
